/**
 * Somebody's own agents, as characters of their own.
 *
 * One person can run several agents at once — one writing code, one on the bug board, one
 * reviewing. Given a name and a key each, they stand in the room as themselves, report their own
 * work, talk to each other in the room chat and dress their own characters, and the person can
 * watch the screens instead of reading logs.
 *
 * The key is shown once, here, and nowhere else. Retiring an agent stops its keys and takes it out
 * of the room; what it did stays on the record, because the work happened.
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, KeyRound, Plus, UserMinus } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { formatRelative } from '../lib/format'
import { CodeBlock } from './primitives'
import { guessSystem, storeKeyCommand } from '../lib/keySetup'

export function MyAgents() {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [issued, setIssued] = useState<{ name: string; key: string } | null>(null)

  const { data: agents } = useQuery({ queryKey: ['my-agents'], queryFn: api.me.agents, retry: false })

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['my-agents'] })

  const create = useMutation({
    mutationFn: () => api.me.createAgent(name.trim()),
    onSuccess: (result) => {
      setIssued({ name: result.name, key: result.key })
      setName('')
      refresh()
    },
  })

  const anotherKey = useMutation({
    mutationFn: (developerId: string) => api.me.issueAgentKey(developerId),
    onSuccess: (result) => setIssued({ name: result.name, key: result.key }),
  })

  const retire = useMutation({ mutationFn: (developerId: string) => api.me.retireAgent(developerId), onSuccess: refresh })

  return (
    <div className="stack">
      <p className="small secondary">{t('agents.what')}</p>

      <form
        className="row row--tight row--wrap"
        onSubmit={(event) => {
          event.preventDefault()
          if (name.trim() && !create.isPending) create.mutate()
        }}
      >
        <input
          className="input"
          value={name}
          onChange={(event) => setName(event.target.value)}
          placeholder={t('agents.namePlaceholder')}
          maxLength={60}
          aria-label={t('agents.name')}
          style={{ maxWidth: 260 }}
        />
        <button type="submit" className="btn btn--sm btn--primary" disabled={!name.trim() || create.isPending}>
          <Plus size={13} aria-hidden /> {t('agents.create')}
        </button>
      </form>

      {create.isError && <span className="field__error">{t('agents.failed')}</span>}

      {issued && (
        <div className="stack stack--tight agents__key">
          <span className="field__label">{t('agents.keyFor', { name: issued.name })}</span>
          <span className="tiny muted">{t('connect.key.once')}</span>
          <CodeBlock code={issued.key} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          <span className="tiny muted">{t('agents.keyHow')}</span>
          <CodeBlock code={storeKeyCommand(guessSystem(), issued.key)} copyLabel={t('common.copy')} copiedLabel={t('common.copied')} />
          <button type="button" className="btn btn--sm" onClick={() => setIssued(null)} style={{ alignSelf: 'flex-start' }}>
            {t('connect.key.done')}
          </button>
        </div>
      )}

      {agents && agents.length > 0 && (
        <ul className="agents__list">
          {agents.filter((agent) => agent.active).map((agent) => (
            <li key={agent.developerId} className="row row--tight small agents__row">
              <Bot size={14} aria-hidden className="muted" />
              <span className="bold truncate">{agent.name}</span>
              <span className="tiny muted nowrap">
                {agent.lastSeenAt ? t('remote.keys.used', { when: formatRelative(agent.lastSeenAt, locale) }) : t('remote.keys.neverUsed')}
              </span>
              <div className="spacer" />
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => anotherKey.mutate(agent.developerId)} title={t('agents.anotherKey')}>
                <KeyRound size={13} aria-hidden /> {t('agents.anotherKey')}
              </button>
              <button type="button" className="btn btn--sm btn--ghost" onClick={() => retire.mutate(agent.developerId)} title={t('agents.retire')}>
                <UserMinus size={13} aria-hidden /> {t('agents.retire')}
              </button>
            </li>
          ))}
        </ul>
      )}

      <span className="tiny muted">{t('agents.hint')}</span>
    </div>
  )
}
